import React from 'react';
import { useTranslation } from 'react-i18next';
import { PigletRegistrationService } from './PigletRegistrationService';
import { PigletRegistrationDashboard } from '../models/piglet-registration';
import { extractApiErrorMessage, notifications } from '../utils';

interface UsePigletRegistrationResult {
  dashboard: PigletRegistrationDashboard | null;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  /** False when the farm turned the widget off, or when the fetch failed. */
  isEnabled: boolean;
  reload: () => void;
}

/**
 * Loads the dashboard's piglet registration data. The cards and the weekly chart render in different places
 * on the page - the existing injection cards sit between them - so the fetch lives in a hook rather than in
 * a component that wraps both.
 *
 * `farmId` picks the source: omitted, the signed-in business unit; set, another farm, which is how the admin
 * farms overview opens a farm without switching the session onto it.
 */
export const usePigletRegistration = (weeks: number, farmId?: string): UsePigletRegistrationResult => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = React.useState<PigletRegistrationDashboard | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    const service = new PigletRegistrationService();
    // The period can change while a request is in flight; ignore anything but the newest one.
    let cancelled = false;

    setIsBusy(true);

    service
      .getDashboard(weeks, farmId)
      .then((result) => {
        if (cancelled) return;
        setDashboard(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setDashboard(null);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [weeks, farmId, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  // Before the first response lands, treat the widget as enabled so the page renders skeletons rather than
  // flashing the layout shut once the answer arrives.
  const isEnabled = !loaded || (dashboard?.isEnabled ?? false);

  return { dashboard, isBusy, loaded, isEnabled, reload };
};

export default usePigletRegistration;
